import type { JSX } from 'react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { THEMES_REGIE, type ThemeRegie } from '@artosera/shared';
import { Button, Switch, useToast } from '@artosera/ui';
import { modifierApparence } from '../../lib/api.js';
import { usePeut } from '../../auth/usePeut.js';
import { estGalerieFermee, type GalerieFiche } from '../../types.js';
import pageStyles from '../../styles/page.module.css';
import styles from './ApparenceTab.module.css';

/** Les noms propres des designs, identiques en français et en anglais. */
const NOMS_THEMES: Record<ThemeRegie, string> = {
  halo: 'Halo',
  structure: 'Structure',
  collection: 'Collection',
  brut: 'Brut',
  atelier: 'Atelier',
  aurore: 'Aurore',
  monolithe: 'Monolithe',
  blanc: 'Blanc',
  spatial: 'Spatial',
  chrome: 'Chrome',
  'liquid-glass': 'Liquid Glass',
  'verre-de-jour': 'Verre de jour',
  'verre-de-nuit': 'Verre de nuit',
};

/**
 * Onglet « Thème de la régie » (lot C). Le design de la régie d'une galerie se
 * choisit ici, pas dans la régie ; l'interrupteur rend — ou retire — à ses
 * utilisateurs le choix de leur design, ce thème restant leur défaut. Le mode
 * clair/sombre et le mode foire ne sont pas concernés.
 *
 * Écrire exige `console:manage-appearance` (exploitation, direction) ; les
 * autres rôles voient les réglages en lecture seule.
 */
export function ApparenceTab({
  idGalerie,
  galerie,
}: {
  idGalerie: string;
  galerie: GalerieFiche;
}): JSX.Element {
  const { t } = useTranslation('console');
  const peut = usePeut();
  const resiliee = estGalerieFermee(galerie.etat);
  const peutRegler = peut('console:manage-appearance') && !resiliee;
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const nom = useId();

  // Le brouillon n'existe que pendant une saisie : sinon, la fiche relue
  // (après enregistrement, ou changée ailleurs) fait foi.
  const [brouillon, setBrouillon] = useState<{
    theme: ThemeRegie;
    choixClient: boolean;
  } | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const theme = brouillon?.theme ?? galerie.theme;
  const choixClient = brouillon?.choixClient ?? galerie.choixThemeClient;
  const setTheme = (suivant: ThemeRegie): void => setBrouillon({ theme: suivant, choixClient });
  const setChoixClient = (suivant: boolean): void => setBrouillon({ theme, choixClient: suivant });

  const modifie = theme !== galerie.theme || choixClient !== galerie.choixThemeClient;

  const mutation = useMutation({
    mutationFn: () => modifierApparence(idGalerie, { theme, choixThemeClient: choixClient }),
    onMutate: () => setErreur(null),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['console', 'galerie', idGalerie] }),
        queryClient.invalidateQueries({ queryKey: ['console', 'galeries'] }),
      ]);
      setBrouillon(null);
      toast({ title: t('fiche.theme.succes'), closeLabel: t('fiche.theme.fermer') });
    },
    onError: () => setErreur(t('fiche.theme.erreur')),
  });

  const desactive = !peutRegler || mutation.isPending;

  return (
    <div className={styles.onglet}>
      {erreur ? (
        <p role="alert" className={`${pageStyles.banniere} ${pageStyles.bannierePeril}`}>
          {erreur}
        </p>
      ) : null}

      {resiliee ? (
        <p className={styles.note}>{t('fiche.theme.resiliee')}</p>
      ) : !peutRegler ? (
        <p className={styles.note}>{t('fiche.theme.lectureSeule')}</p>
      ) : null}

      <section className={pageStyles.panneau}>
        <fieldset className={styles.themes} aria-describedby={`${nom}-texte`}>
          <legend>{t('fiche.theme.titre')}</legend>
          <p id={`${nom}-texte`} className={styles.texte}>
            {t('fiche.theme.texte')}
          </p>
          <div className={styles.grille}>
            {THEMES_REGIE.map((option) => (
              <label
                key={option}
                className={styles.option}
                data-selected={theme === option}
                data-disabled={desactive}
                data-theme-option={option}
              >
                <input
                  type="radio"
                  name={nom}
                  value={option}
                  checked={theme === option}
                  disabled={desactive}
                  onChange={() => setTheme(option)}
                  aria-describedby={`${nom}-${option}`}
                />
                <span className={styles.copie}>
                  <span className={styles.nom}>{NOMS_THEMES[option]}</span>
                  <span id={`${nom}-${option}`} className={styles.description}>
                    {t(`fiche.theme.descriptions.${option}`)}
                  </span>
                </span>
                <Check size={17} className={styles.coche} aria-hidden="true" />
              </label>
            ))}
          </div>
        </fieldset>
      </section>

      <section className={pageStyles.panneau}>
        <Switch
          id={`${nom}-choix`}
          label={t('fiche.theme.choixClient')}
          checked={choixClient}
          disabled={desactive}
          onCheckedChange={setChoixClient}
          aria-describedby={`${nom}-choix-aide`}
        />
        <p id={`${nom}-choix-aide`} className={styles.aide}>
          {t('fiche.theme.choixClientAide')}
        </p>
      </section>

      {peutRegler ? (
        <div className={styles.actions}>
          <Button
            disabled={!modifie}
            loading={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? t('fiche.theme.enCours') : t('fiche.theme.enregistrer')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
